<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-overlay :value="isRequestingNow">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-flex>
        <filter-users
          :default-search-params="searchParams"
          @filtering="onFilter"
        ></filter-users>
        <v-card v-if="!users.length" class="pa-3 my-4">
          <p class="text-center mt-2">
            <v-icon>mdi-account-search-outline</v-icon>
            <b
            >&nbsp;&nbsp;{{
                lang($store, 'sap/listings.not_found')
              }}&nbsp;&nbsp;</b
            >
            <v-icon>mdi-account-badge-outline</v-icon>
          </p>
        </v-card>
        <v-row class="mt-8">
          <v-col
            cols="12"
            md="4"
            xl="3"
            v-for="(item, key) in users"
            :key="key"
          >
            <v-card
              :color="cardColor(item)"
              class="mx-auto mb-2"
              min-height="200"
              outlined
            >
              <v-card-subtitle class="overline d-flex justify-space-between" :style="{ color: cardTextColor(item) }">
                <div class="d-block d-sm-inline-block">
                  {{ $t('enums.roles.'+ item.role)}}
                </div>
                <div v-if="item.region">
                  {{ $t('enums.region.'+item.region)}}
                </div>
              </v-card-subtitle>
              <v-card-text>
                <v-list-item-content
                  style="position: relative"
                  :style="{ color: cardTextColor(item) }"
                >
                  <v-list-item-title
                    class="font-weight-bold d-flex justify-space-between"
                  >
                    <div>{{ item.name }}</div>
                    <phone-chip
                      small
                      :color="cardTextColor(item)"
                      :text-color="cardTextColor(item)"
                      :phone="item.phone"
                    />
                  </v-list-item-title>
                  <v-divider class="my-2" :color="cardTextColor(item)"/>
                  <v-list-item-subtitle :style="{ color: cardTextColor(item) }">
                    {{ item.note }}
                  </v-list-item-subtitle>
                  <div v-if="item.email" class="caption">
                    {{ $t('deleted_users.email') }}: {{ item.email }}
                  </div>
                  <div v-if="item.address" class="caption">
                    {{ $t('deleted_users.address') }}: {{ item.address }}
                  </div>
                  <div v-if="item.phone2" class="caption">
                    {{ $t('deleted_users.phone') }} 2: {{ item.phone2 }}
                    <span class="ml-2" v-if="item.phone2_note"
                    >({{ item.phone2_note }})</span
                    >
                  </div>
                </v-list-item-content>
              </v-card-text>
              <v-card-actions>
                <div :style="{ color: cardTextColor(item) }">
                  {{ item.deleted }}
                </div>
                <v-spacer/>
                <v-btn
                  text
                  outlined
                  :color="cardTextColor(item)"
                  @click="onItemRestore(item)"
                >{{ $t('deleted_users.restore') }}
                </v-btn
                >
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-flex>
      <accept-dialog
        :is-open-dialog="isShowRestoreDialog"
        @on-close="isShowRestoreDialog = false"
        @on-accept="onAcceptRestoring"
        max-width="320"
      >
        <div class="pt-6">
          {{ acceptModalText }}
        </div>
      </accept-dialog>
    </v-layout>
  </v-container>
</template>

<script>
import _ from 'lodash';
/* constants */
/* utils */
import { strReplaceAll } from '@/utils/index';
/* proxies */
import UserProxy from '@/proxies/User/UserProxy';
/* mixins */
import defaultListingMixin from '@/mixins/listing/defaultListingMixin';
import updatePageContentMixin from '@/mixins/updatePageContentMixin';
import cardColorsMixin from '@/mixins/cardColorsMixin';

/* components */
import FilterUsers from '@/components/listings/FilterUsers.vue';
import AcceptDialog from '@/components/AcceptDialog.vue';
import PhoneChip from '@/components/minialerts/PhoneChip.vue';
import PremiseChip from '@/components/minialerts/PremiseChip.vue';

export default {
  name: 'DeletedUsers',
  components: {
    AcceptDialog,
    FilterUsers,
    PhoneChip,
    PremiseChip,
  },
  mixins: [updatePageContentMixin, defaultListingMixin, cardColorsMixin],
  data() {
    return {
      fetchDataOnCreated: true,
      users: [],
      isLoadingUserDialog: false,
      errorsUserDialog: {},
      isShowUserDialog: false,
      currentRestoredItem: {},
      isShowRestoreDialog: false,
      searchParams: {
        search: '',
        role: null,
        region: null,
      },
    };
  },
  computed: {
    /* accept modal */
    acceptModalText() {
      return strReplaceAll(
        this.lang(this.$store, 'sap/listings.global.restore'),
        {
          user: this.currentRestoredItem.name,
        },
      );
    },
    /* store */
    account() {
      return this.$store.getters['account/account'];
    },
  },
  methods: {
    fetchData() {
      this.isRequestingNow = true;
      new UserProxy()
        .setParameters(this.searchParams)
        .deleted()
        .then(response => {
          this.users = response.data.data || [];
        })
        .catch(error => {
          console.warn(error);
          this.users = [];
        })
        .finally(() => {
          this.isRequestingNow = false;
        });
    },
    onItemRestore(item) {
      this.currentRestoredItem = item;
      this.isShowRestoreDialog = true;
    },
    /**
     * @see defaultListingMixin
     * @param newSearchParams
     * @returns {*}
     */
    filterSearchParamsBeforeSend(newSearchParams) {
      if (_.isString(newSearchParams.role)) {
        // eslint-disable-next-line no-param-reassign
        newSearchParams.role = [newSearchParams.role];
      }
      if (_.isString(newSearchParams.region)) {
        // eslint-disable-next-line no-param-reassign
        newSearchParams.region = [newSearchParams.region];
      }
      return newSearchParams;
    },
    onCloseUserDialog() {
      this.isShowUserDialog = false;
    },
    onAcceptRestoring() {
      this.isRequestingNow = true;
      new UserProxy()
        .restore(this.currentRestoredItem.uuid)
        .catch(response => {
          console.error(response);
          this.isRequestingNow = false;
        })
        .then(() => {
          this.fetchData();
        });
    },
  },
};
</script>
